// components/TermsPage.jsx
import React from 'react';
import { theme } from '../theme';

export function TermsPage({ onBack }) {
  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: theme.text }}>
      <nav style={{ maxWidth: '900px', margin: '0 auto', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '32px', fontWeight: 400, color: theme.teal, margin: 0, cursor: 'pointer' }} onClick={onBack}>InsightStock</h1>
        <button onClick={onBack} style={{ background: 'transparent', border: '1px solid ' + theme.border, borderRadius: '8px', padding: '10px 20px', color: theme.text, fontSize: '16px', cursor: 'pointer' }}>← Back</button>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px 80px' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '42px', fontWeight: 400, color: theme.text, margin: '0 0 16px' }}>Terms of Service</h1>
        <p style={{ color: theme.textDark, fontSize: '16px', margin: '0 0 48px' }}>Last updated: January 23, 2026</p>

        <Section title="1. Agreement to Terms">
          By accessing or using InsightStock ("Service"), operated by Insight Mind AI LLC ("we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
        </Section>

        <Section title="2. Description of Service">
          InsightStock is an AI-powered stock research platform that provides market data, technical signals, news analysis, and portfolio review tools. The Service is designed to help investors organize information and identify potential expectation shifts in U.S. stocks.
        </Section>

        <Section title="3. Not Financial Advice">
          <strong>The Service is for informational purposes only and does not constitute financial, investment, legal, or tax advice.</strong> We are not a registered investment advisor, broker-dealer, or financial planner. All information provided through the Service, including AI-generated analysis, should not be relied upon as a basis for investment decisions. Always consult with a qualified financial professional before making any investment decisions.
        </Section>

        <Section title="4. AI-Generated Content">
          Our Service uses artificial intelligence to analyze market data and generate insights. AI-generated content may contain errors, inaccuracies, or outdated information. You acknowledge that:
          {'\n\n'}• AI analysis is not a substitute for professional financial advice
          {'\n'}• We do not guarantee the accuracy, completeness, or timeliness of AI-generated content
          {'\n'}• You are solely responsible for verifying information with official sources before making decisions
        </Section>

        <Section title="5. User Accounts">
          To access certain features, you must create an account. You agree to:
          {'\n\n'}• Provide accurate and complete information
          {'\n'}• Maintain the security of your account credentials
          {'\n'}• Notify us immediately of any unauthorized access
          {'\n'}• Accept responsibility for all activities under your account
        </Section>

        <Section title="6. Subscription and Payments">
          Paid subscriptions (Pro and Pro+) are billed monthly or annually through Stripe. By subscribing, you agree that:
          {'\n\n'}• Subscriptions auto-renew unless canceled before the renewal date
          {'\n'}• Refunds are provided at our sole discretion
          {'\n'}• We may change pricing with 30 days notice to existing subscribers
          {'\n'}• You can cancel anytime through your account settings
        </Section>

        <Section title="7. Acceptable Use">
          You agree not to:
          {'\n\n'}• Use the Service for any unlawful purpose
          {'\n'}• Attempt to reverse engineer, scrape, or copy our data or algorithms
          {'\n'}• Share your account credentials with others
          {'\n'}• Use automated systems to access the Service without permission
          {'\n'}• Resell or redistribute any content from the Service
        </Section>

        <Section title="8. Intellectual Property">
          All content, features, and functionality of the Service are owned by Insight Mind AI LLC and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute any part of the Service without our written permission.
        </Section>

        <Section title="9. Data and Privacy">
          Your use of the Service is also governed by our Privacy Policy. By using the Service, you consent to our collection and use of data as described in the Privacy Policy.
        </Section>

        <Section title="10. Portfolio Review Feature">
          If you use our Portfolio Review feature (Pro+), you may upload trade history files. You acknowledge that:
          {'\n\n'}• You are responsible for the accuracy of uploaded data
          {'\n'}• We process your data solely to provide the Service
          {'\n'}• Analysis of your trades is for informational purposes only
          {'\n'}• We do not store your uploaded files longer than necessary to provide the Service
        </Section>

        <Section title="11. Limitation of Liability">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, INSIGHT MIND AI LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR INVESTMENT LOSSES ARISING FROM YOUR USE OF THE SERVICE.
          {'\n\n'}Our total liability for any claim arising from the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
        </Section>

        <Section title="12. Disclaimer of Warranties">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
        </Section>

        <Section title="13. Indemnification">
          You agree to indemnify and hold harmless Insight Mind AI LLC from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
        </Section>

        <Section title="14. Changes to Terms">
          We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on the Service. Your continued use after changes constitutes acceptance of the new Terms.
        </Section>

        <Section title="15. Termination">
          We may suspend or terminate your access to the Service at any time for any reason, including violation of these Terms. Upon termination, your right to use the Service ceases immediately.
        </Section>

        <Section title="16. Governing Law">
          These Terms are governed by the laws of the State of Wyoming, United States, without regard to conflict of law principles.
        </Section>

        <Section title="17. Contact Us">
          If you have questions about these Terms, contact us at:
          {'\n\n'}Insight Mind AI LLC
          {'\n'}1621 Central Ave
          {'\n'}Cheyenne, WY 82001
          {'\n'}Email: support@insightstock.io
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: 400, color: theme.teal, margin: '0 0 16px' }}>{title}</h2>
      <p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.8', margin: 0, whiteSpace: 'pre-line' }}>{children}</p>
    </div>
  );
}

export default TermsPage;